import config from '@/config';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
} from '@tanstack/react-router';
import React from 'react';
import { createPortal } from 'react-dom';
import { type AuthContextData } from 'src/context/auth.context';
import NotFoundPage from './NotFoundPage';
import { QueryClient } from '@tanstack/react-query';
import type { ModuleEnabledFunction } from '@/hooks/organization-config/useOrganizationEnabledModules';
import type { AuthzClient } from '@/context/authz/AuthzClient';
import { cn } from '@/lib/utils';
import { useLocalStorageValue } from '@react-hookz/web';
import { z } from 'zod';
import { buildTabTitle } from '@/lib/tabTitle';
import { decodeRouteMessage } from '@/routing/route-messages';
import { readCachedOrganizationConfig } from '@/api/core/organization-config/useOrganizationConfig';

const LoaderCrumbSchema = z.object({
  crumb: z.string().optional(),
});

if (config.isDev) {
  // @ts-expect-error - just a devtools thing, only affects development
  window.toggleDevtools = function toggleDevtools() {
    const hideDevtools =
      localStorage.getItem('GENTLY_HIDE_DEVTOOLS') === 'true';
    localStorage.setItem(
      'GENTLY_HIDE_DEVTOOLS',
      hideDevtools ? 'false' : 'true',
    );
    window.location.reload();
  };
}

export interface RouterContext {
  auth: AuthContextData;
  queryClient: QueryClient;
  isModuleEnabled: ModuleEnabledFunction;
  authzClient: AuthzClient;
}

const TanStackRouterDevtools = config.isDev
  ? React.lazy(() =>
      import('@tanstack/react-router-devtools').then((res) => ({
        default: res.TanStackRouterDevtools,
      })),
    )
  : (): null => null;

function RootRoute() {
  const { value: hideDevtools } = useLocalStorageValue<boolean>(
    'GENTLY_HIDE_DEVTOOLS',
    {
      defaultValue: true,
    },
  );

  return (
    <>
      {createPortal(<HeadContent />, document.head)}
      <Outlet />
      {!hideDevtools && (
        <TanStackRouterDevtools
          position="bottom-right"
          initialIsOpen={false}
          toggleButtonProps={{
            className: cn('opacity-50 hover:opacity-100'),
          }}
        />
      )}
    </>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootRoute,
  notFoundComponent: () => <NotFoundPage />,
  head: (ctx) => {
    // Find the deepest route with a crumb in loaderData
    const lastMatchWithCrumb = [...ctx.matches].reverse().find((m) => {
      const result = LoaderCrumbSchema.safeParse(m.loaderData);
      return result.success && result.data.crumb;
    });

    const parsedLoaderData = LoaderCrumbSchema.safeParse(
      lastMatchWithCrumb?.loaderData,
    );
    // Crumbs are `msg()`-encoded authored copy, so the raw value carries a
    // base64 metadata blob (`$_hash`) after the source text. `head` runs
    // outside React, so decode to the source locale rather than via `m()`.
    const rawCrumb = parsedLoaderData.success
      ? parsedLoaderData.data.crumb
      : undefined;
    const pageTitle = rawCrumb ? decodeRouteMessage(rawCrumb) : undefined;

    // `head` runs outside React, so the org's tab label comes from the query
    // cache rather than a hook. It is already populated by the time the router
    // mounts — index.tsx renders the app skeleton until the org config (and
    // with it the enabled modules) has loaded.
    const orgConfig = readCachedOrganizationConfig(
      ctx.match.context.queryClient,
    );

    return {
      meta: [
        {
          title: buildTabTitle({
            appName: config.appName,
            label: config.devTabTitleLabel || orgConfig?.tabTitleLabel,
            pageTitle,
          }),
        },
      ],
    };
  },
});
